/**
 * DON'T OFFER THE ACTION, THEN REJECT IT (Layne, ASK-JACK-DISABLE-CONTROLS-20260903).
 *
 * Every block on the canvas shows ↑ ↓ ⧉. For most blocks, most of the time, at least one
 * of those can never succeed: 26 of her 32 blocks declare `maxCount: 1`, so their
 * duplicate button is a permanently dead control; the header can never move up; the
 * footer trio can never move at all; the CTA row travels with Body Copy so its own arrows
 * mislead. The Studio already knew every one of those rules — it read them AFTER the
 * click, to write the refusal toast. Her ask: run the same check against the CANDIDATE
 * action and show the answer on the control, with the rule's own sentence as the tooltip.
 *
 * Nothing here is a new rule. `reorder` and `capViolation` are the two functions the click
 * handlers call; this module dry-runs them for each control and hands back the reason a
 * control is dead, or nothing when it is live. The click handlers keep calling the real
 * thing, so a stale render can never let a refused action through.
 *
 * DISABLED, NOT HIDDEN — her preference: "Hiding leaves an author wondering why this
 * module has two buttons and that one has three. Disabling, with the rule's message as a
 * tooltip, teaches the constraint at the moment they reach for it."
 */
import { capViolation, type CapBlock } from "./blockCaps";
import { bracketRider, followOf, isPinned, nameOf, reorder, type PlaceBlock } from "./echPlacement";
import { echByName } from "./echRegistry";

export interface ControlState {
  /** why ↑ is dead, or undefined when it is live */
  up?: string;
  /** why ↓ is dead, or undefined */
  down?: string;
  /** why ⧉ is dead, or undefined */
  dup?: string;
  /** why ✕ is dead, or undefined — a pinned block cannot be removed (Layne, 2026-09-28) */
  delete?: string;
}

/** the block's declared label, for a sentence about it */
function labelOf(name: string): string {
  const m = echByName(name);
  const l = m && ((m.gr && m.gr.label) || (m.full && m.full.label));
  return String(l || name.replace(/^ECH_/, "") || "this block");
}

/**
 * The state of the three block-bar controls for block `id` in `doc`.
 *
 * ↑ / ↓: the same `reorder` the arrow calls, dry-run one step in that direction (with
 * the group step-over, so an arrow that can hop a pair stays live). At the top or bottom
 * of the document the arrow is dead with a plain reason. A block that RIDES with another
 * (`movesWith`, the CTA row) has both arrows dead with the rule's sentence — the pair is
 * moved from the block it follows, and an arrow on the follower is, in Layne's words,
 * "misleading rather than merely useless".
 *
 * ⧉: the same `capViolation` the duplicate button calls.
 */
export function controlState(doc: (PlaceBlock & CapBlock)[] | null | undefined, id: string, tplName?: string): ControlState {
  const d = doc || [];
  const i = d.findIndex((b) => b.id === id);
  if (i < 0) return {};
  const b = d[i];
  const out: ControlState = {};

  const f = followOf(b, tplName);
  const rides = !!(f && f.movesWith && i > 0 && nameOf(d[i - 1]) === f.movesWith);
  const bracket = bracketRider(d, i);   /* a 25px spacer touching the Community subhead (2026-09-17) */
  if (rides) {
    const why = (f && f.message && f.message.length <= 120 ? f.message : "") || ("Moves with " + labelOf(f!.movesWith) + " — use its arrows.");
    out.up = why;
    out.down = why;
  } else if (bracket) {
    out.up = bracket;
    out.down = bracket;
  } else {
    if (i === 0) out.up = "Already the first block.";
    else { const r = reorder(d, id, i - 1, tplName); if (r.message) out.up = r.message; }
    if (i === d.length - 1) out.down = "Already the last block.";
    else { const r = reorder(d, id, i + 1, tplName); if (r.message) out.down = r.message; }
  }

  const cap = capViolation(d, b.type, tplName);
  if (cap) out.dup = cap;

  /* ── ✕ IS GOVERNED TOO (Layne, ASK-JACK-PIN-DELETE-GUARD-20260928) ──────────────────
     A pinned block (a template `lockedPosition` pin, or the block's own
     `placement: {locked, position}`) could still be deleted, and the blocks below it were
     then stuck one index short of their own pins: arrows dead, drag gone, and re-inserting
     the missing block refused for the problems that creates — an email nobody could
     repair. Deleting is the only way into that state, so the same pin that kills the
     arrows kills the ✕. Nothing here names a block: whatever Layne pins is covered. */
  if (isPinned(b, tplName)) out.delete = labelOf(nameOf(b)) + " is fixed in place in this template, so it can't be removed.";
  return out;
}
